import { MutableRefObject, useCallback } from 'react';
import { scaleLinear, scaleTime } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { area, curveMonotoneX, line } from 'd3-shape';
import { isFinite, isNil } from 'lodash';
import { useUnmount } from 'react-use';

import type { CrowdTimestep } from 'types/crowd';

import styles from '../CrowdGraph.module.scss';
import graphStyles from '../../../../Graphs.module.scss';

interface GraphDataPoint {
  timestamp: number;
  crowd: number | null;
}

const getPredictedGraphData = (data: Array<CrowdTimestep | undefined> | undefined) => {
  const graphDataPredicted: Array<GraphDataPoint> | undefined = data?.map((dataPoint) => ({
    ...dataPoint,
    timestamp: dataPoint?.timestamp ?? 0,
    crowd: dataPoint?.crowd?.normalized ?? null,
  }));

  const firstTimestamp = graphDataPredicted?.[0].timestamp;

  const graphRange = {
    start: firstTimestamp ?? 0,
    end: graphDataPredicted?.[graphDataPredicted.length - 1].timestamp ?? 0,
  };

  return { graphDataPredicted, graphRange };
};

const getObservedGraphData = (data: Array<CrowdTimestep | undefined> | null) => {
  const graphData: Array<GraphDataPoint> | undefined = data?.map((dataPoint) => ({
    ...dataPoint,
    timestamp: dataPoint?.timestamp ?? 0,
    crowd: dataPoint?.crowd?.normalized ?? null,
  }));

  let currentPath: Array<GraphDataPoint> = [];
  const graphDataObserved: Array<Array<GraphDataPoint>> = [];

  graphData?.forEach((dataPoint) => {
    if (dataPoint.crowd !== null) {
      currentPath.push(dataPoint);
    } else if (currentPath.length > 0) {
      graphDataObserved.push(currentPath);
      currentPath = [];
    }
  });
  if (currentPath.length > 0) graphDataObserved.push(currentPath);
  const lastObservedPoint: GraphDataPoint | undefined =
    graphDataObserved[graphDataObserved.length - 1]?.[
      graphDataObserved[graphDataObserved.length - 1].length - 1
    ];

  return {
    graphDataObserved,
    lastObservedPoint,
  };
};

interface Props {
  canvasRef: MutableRefObject<HTMLDivElement | null>;
  data: {
    observed: Array<CrowdTimestep | undefined> | null;
    predicted: Array<CrowdTimestep | undefined> | undefined;
  };
  graphHeight?: number;
  graphWidth?: number;
}

const useCrowdGraph = ({ canvasRef, data, graphHeight, graphWidth }: Props) => {
  const setupGraph = useCallback(() => {
    if (
      canvasRef?.current &&
      graphHeight &&
      graphWidth &&
      isFinite(graphHeight) &&
      isFinite(graphWidth)
    ) {
      selectAll(`.camera-insights-crowd-graph-svg`).remove();
      const GRAPH_PAD_BOTTOM = 1;
      const MAX_CROWD = 100;
      const { graphDataPredicted, graphRange } = getPredictedGraphData(data?.predicted);
      if (isNil(graphDataPredicted)) return;

      const { graphDataObserved, lastObservedPoint } = getObservedGraphData(data?.observed);

      const x = scaleTime().range([0, graphWidth]).domain([graphRange.start, graphRange.end]);

      const y = scaleLinear()
        .range([graphHeight - GRAPH_PAD_BOTTOM, GRAPH_PAD_BOTTOM])
        .domain([0, MAX_CROWD]);

      // https://observablehq.com/@d3/area-chart
      const drawArea = area<GraphDataPoint>()
        .x((d) => x(d.timestamp))
        .y0(y(0)) // Set the bottom y-coordinate to the y-coordinate of the bottom axis
        .y1((d) => y(d?.crowd ?? 0)) // Set the top y-coordinate to the y-coordinate of the data point
        .curve(curveMonotoneX);

      const drawLine = line<GraphDataPoint>()
        .x((d: GraphDataPoint) => x(d.timestamp))
        .y((d: GraphDataPoint) => y(d.crowd as number))
        .curve(curveMonotoneX);

      const canvas = select(canvasRef.current);

      const svg = canvas
        .append('svg')
        .attr('height', graphHeight)
        .attr('width', graphWidth)
        .attr('class', 'camera-insights-crowd-graph-svg');

      const crowdGroup = svg.append('g').attr('class', 'swell');

      // CROWD GRADIENT AREA FILL
      const gradient = crowdGroup
        .append('defs')
        .append('linearGradient')
        .attr('id', 'crowdAreaGradient')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', 60);
      // bottom of gradient
      gradient.append('stop').attr('offset', '0%').attr('stop-color', 'rgba(185, 211, 223, 0.9)');
      // top of gradient
      gradient.append('stop').attr('offset', '100%').attr('stop-color', 'rgba(185, 211, 223, 0.4)');

      // CROWD GRADIENT AREA
      crowdGroup
        .append('path')
        .style('fill', 'url(#crowdAreaGradient)')
        .attr('d', drawArea(graphDataPredicted))
        .attr('data-testid', 'camera-insights-crowd-predicted-area');

      // OBSERVED LINE - only draw if we have observed paths
      if (!isNil(graphDataObserved) && graphDataObserved.length > 0) {
        graphDataObserved.forEach((pathToDraw) => {
          crowdGroup
            .append('path')
            .datum(pathToDraw)
            .attr('d', drawLine as any)
            .attr('class', styles.observedLine)
            .attr('data-testid', 'camera-insights-crowd-observed-line');
        });
      }

      // LAST OBSERVED DATA POINT - only draw if we have data on our last observed data point
      if (!isNil(lastObservedPoint) && !isNil(lastObservedPoint?.crowd)) {
        crowdGroup
          .append('line')
          .attr('x1', x(lastObservedPoint.timestamp))
          .attr('y1', y(lastObservedPoint.crowd))
          .attr('x2', x(lastObservedPoint.timestamp))
          .attr('y2', graphHeight - GRAPH_PAD_BOTTOM)
          .attr('class', styles.observedLastPointLine)
          .attr('data-testid', 'camera-insights-crowd-observed-last-point-line');
        crowdGroup
          .append('circle')
          .attr('cx', x(lastObservedPoint.timestamp))
          .attr('cy', y(lastObservedPoint.crowd))
          .attr('r', 3)
          .attr('class', styles.observedLastPoint)
          .attr('data-testid', 'camera-insights-crowd-observed-last-point');
      }

      // X-AXIS DIVIDER LINE
      crowdGroup
        .append('line')
        .attr('x1', 0)
        .attr('y1', graphHeight - GRAPH_PAD_BOTTOM)
        .attr('x2', graphWidth)
        .attr('y2', graphHeight - GRAPH_PAD_BOTTOM)
        .attr('class', graphStyles.xAxisDivider);

      // Y-AXIS DIVIDER LINES
      Array.from({ length: 2 }).forEach((_, i) => {
        const yPosition = (graphHeight - GRAPH_PAD_BOTTOM) * (i === 0 ? 0.2 : 0.75);

        crowdGroup
          .append('line')
          .attr('x1', 12)
          .attr('y1', yPosition)
          .attr('x2', graphWidth)
          .attr('y2', yPosition)
          .attr('class', graphStyles.yAxisTick);

        crowdGroup
          .append('text')
          .attr('x', 0)
          .attr('y', yPosition)
          .text(i === 0 ? 'Busier' : 'Less Busy')
          .attr('class', graphStyles.yAxisLabel);
      });
    }
  }, [canvasRef, data, graphHeight, graphWidth]);

  useUnmount(() => {
    selectAll('.camera-insights-crowd-graph-svg').remove();
  });

  return { setupGraph };
};

export default useCrowdGraph;
